import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Item } from '@/types/item';
import { Modal } from '@/types/modal';
import { GridAction } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import ModalView from '@/components/modals/Modal';
import { useActionTracking, useItem, usePostalLitigation } from '@/hooks/item';
import { LoadingBodyModal } from '@/components/molecules/common/Modal';
import { transformDateStringFrance } from '@/utilities/datetime';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import Table from '@/components/molecules/section/Table';
import { BootService } from '@/services/boot';
import { environments } from '@/environment';
import ValueList from '@/components/atoms/ValueList';
import { LitigationType } from '@/types/omsStatus';

const INTERMEDIATE_DECISIONS = ['INTERMEDIATE_1', 'INTERMEDIATE_2'];

const FINAL_DECISIONS = ['FINAL'];

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const [open, setOpen] = useState(false);
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);

  const data = usePostalLitigation({
    productId: item.productId.toString(),
    enabled: open,
  });

  const mutateTracking = useActionTracking(item.productId);

  const { refetch: refetchItem } = useItem(item.productId, false);

  useEffect(() => {
    if (mutateTracking.kind === 'Error') {
      setModal({
        kind: 'Error',
        title: t('item.postal_litigation.title'),
        message: t('item.postal_litigation.tracking_failed'),
        onClose: () => setModal(null),
      });
    } else if (mutateTracking.kind === 'Completed') {
      refetchItem();
    }
  }, [mutateTracking.kind]);

  const podLabelUrl = item.shipmentInfo?.podLabelUrl;

  const actions: GridAction[] = [
    {
      kind: 'Link',
      label: t('item.postal_litigation.actions.pod'),
      disabled: !podLabelUrl,
      url: podLabelUrl || '',
      newTab: true,
      onClick: () => {
        if (podLabelUrl) {
          mutateTracking.mutate('download_product_proof_of_delivery');
        }
      },
    },
    {
      kind: 'Button',
      label: t('item.postal_litigation.actions.plConfirmation'),
      onClick: () => setModal({ kind: 'ItemPLConfirmation', item, onClose: () => setModal(null) }),
    },
    {
      kind: 'Button',
      label: t('item.postal_litigation.actions.plDecision'),
      onClick: () => setModal({ kind: 'ItemPLDecision', item, onClose: () => setModal(null) }),
    },
    {
      kind: 'Button',
      label: t('oms_return_status.actions.create_seller_portal_litigation'),
      onClick: () => {
        setModal({
          kind: 'ItemCreateReturnLitigation',
          litigationType: LitigationType.POSTAL_SELLER,
          item,
          onClose: () => setModal(null),
        });
      },
    },
    {
      kind: 'Button',
      label: t('oms_return_status.actions.create_buyer_portal_litigation'),
      onClick: () => {
        setModal({
          kind: 'ItemCreateReturnLitigation',
          litigationType: LitigationType.POSTAL_BUYER,
          item,
          onClose: () => setModal(null),
        });
      },
    },
    {
      kind: 'Button',
      label: t('oms_return_status.actions.create_replacement_zendesk_ticket'),
      onClick: () => {
        setModal({
          kind: 'ItemCreateReturnLitigation',
          litigationType: LitigationType.REPLACEMENT_ZENDESK_TICKET,
          item,
          onClose: () => setModal(null),
        });
      },
    },
  ];

  const postalLitigation = data.kind === 'Loaded' ? data.data : {};

  const columns: GridColDef[] = [
    {
      field: 'ticketId',
      headerName: t('item.postal_litigation.zendesk_ticket'),
      flex: 1,
      renderCell: (params) => (
        <ValueList
          values={params.value || []}
          onItemClick={(ticket) => {
            const path = environments[BootService.env].zendeskUrl;
            window.open(`${path}/agent/tickets/${ticket}`);
          }}
        />
      ),
    },
    { field: 'creationDate', headerName: t('item.postal_litigation.creation_date'), flex: 1 },
    { field: 'daysInProgress', headerName: t('item.postal_litigation.days_in_progress'), flex: 1 },
    { field: 'closeDate', headerName: t('item.postal_litigation.close_date'), flex: 1 },
    {
      field: 'intermediateDecisions',
      headerName: t('item.postal_litigation.plIntermediateDecision'),
      flex: 1,
      renderCell: (params) => <ValueList values={params.value || []} />,
    },
    {
      field: 'finalDecisions',
      headerName: t('item.postal_litigation.plFinalDecision'),
      flex: 1,
      renderCell: (params) => <ValueList values={params.value || []} />,
    },
    { field: 'workflowStatus', headerName: t('item.postal_litigation.workflow_status'), flex: 1 },
  ];

  const rows = useMemo(() => {
    if (!postalLitigation.litigations?.length) return [];

    return postalLitigation.litigations.slice((page - 1) * pageSize, page * pageSize).map((data, index) => ({
      id: `${index}`,
      ticketId: data.litigationNumbers || [],
      creationDate: transformDateStringFrance(data.startedAt, 'DD/MM/YYYY') || '-',
      daysInProgress: data.daysInProgress || '-',
      closeDate:
        transformDateStringFrance(data.startedAt && data.concludedAt ? data.concludedAt : null, 'DD/MM/YYYY') || '-',
      intermediateDecisions:
        data.decisions
          ?.filter((d) => d.decisionType && d.label && INTERMEDIATE_DECISIONS.includes(d.decisionType))
          .map((d) => d.label) || [],
      finalDecisions:
        data.decisions
          ?.filter((d) => d.decisionType && d.label && FINAL_DECISIONS.includes(d.decisionType))
          .map((d) => d.label) || [],
      workflowStatus: data.workflowStatus || '-',
    }));
  }, [postalLitigation, page, pageSize]);

  const onPageChange = ({ page, pageSize }: GridPaginationModel) => {
    setPage(page + 1);
    setPageSize(pageSize);
  };

  return (
    <>
      <Container
        title={t('item.postal_litigation.title')}
        actions={actions}
        testId="postal_litigation"
        handler={{ open, setOpen }}
      >
        {open && data.kind === 'Called' ? (
          <LoadingBodyModal />
        ) : (
          <Table
            rows={rows}
            columns={columns}
            loading={data.kind === 'Called' && data.fetching}
            table={{
              kind: 'DynamicTable',
              totalItem: postalLitigation.litigations?.length || 0,
              pageSize,
              pageSizeOptions: [5, 10, 15, 20],
              onPageChange,
            }}
          />
        )}
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
